/* Independent PARK locator. The compiled store-locator custom element stays unused. */
(function () {
  'use strict';
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim();
  const validPosition = p => Number.isFinite(p.lat) && Number.isFinite(p.lng) && Math.abs(p.lat) <= 90 && Math.abs(p.lng) <= 180 && !(p.lat === 0 && p.lng === 0);
  function sortStores(stores) {
    const order = store => { const raw = store.order; const number = Number(raw); return raw !== undefined && raw !== null && String(raw).trim() !== '' && Number.isFinite(number) ? number : 1000; };
    return stores.map((store, index) => ({ store, index })).sort((a, b) => order(a.store) - order(b.store) || a.index - b.index).map(entry => entry.store);
  }
  function pinOptions(data, baseURL, mobile = false) {
    const options = { scale: 1.2, background: data.pinbackground, borderColor: data.pinbordercolor, glyphColor: data.pinglyphcolor };
    const glyphImage = mobile && data.pinglyphimgMobile ? data.pinglyphimgMobile : data.pinglyphimg;
    if (glyphImage && glyphImage.trim()) {
      try {
        const url = new URL(glyphImage, baseURL);
        if (url.protocol === 'https:' && !url.username && !url.password) options.glyphSrc = url.href;
      } catch (_error) { /* Keep the configured glyph colour when an image URL is invalid. */ }
    }
    return options;
  }
  function clearMap(owner) {
    owner.info?.close(); owner.markers?.forEach(marker => { marker.map = null; });
    owner.markers = []; owner.map = null; owner.info = null;
  }
  function sortByDistance(stores, position) {
    const distance = store => validPosition(store.position) ? Math.hypot(store.position.lat - position.lat, store.position.lng - position.lng) : Infinity;
    return stores.map((store, index) => ({ store, index })).sort((a, b) => distance(a.store) - distance(b.store) || a.index - b.index).map(entry => entry.store);
  }
  function requestLocalPosition(geolocation, state, isAlive, onSorted) {
    if (!geolocation) return;
    const token = state.revision();
    try {
      geolocation.getCurrentPosition(result => {
        const position = { lat: result.coords.latitude, lng: result.coords.longitude };
        if (!isAlive() || !validPosition(position) || !state.current(token)) return;
        state.orderByPosition(position); onSorted(position);
      }, () => {}, { timeout: 8000, maximumAge: 300000, enableHighAccuracy: false });
    } catch (_error) { /* Denied/unavailable location keeps the immediately visible default order. */ }
  }
  function createState(stores) {
    let revision = 0;
    return {
      visible: stores.slice(), region: 'all', view: 'list',
      revision() { return revision; },
      orderByPosition(position) { const ordered = sortByDistance(stores, position); stores.splice(0, stores.length, ...ordered); this.visible = ordered.slice(); },
      invalidate() { return ++revision; },
      current(token) { return token === revision; },
      reset(region = 'all') { ++revision; this.region = region; this.view = 'list'; this.visible = stores.filter(s => region === 'all' || s.region === region); },
      select(id) { ++revision; this.view = 'map'; this.visible = stores.filter(s => s.id === id); },
      search(query) { ++revision; this.visible = stores.filter(s => normalize(s.text).includes(normalize(query))); return this.visible; },
      nearby(position, token) {
        if (!this.current(token)) return false;
        this.view = 'map';
        this.visible = stores.filter(s => validPosition(s.position) && Math.abs(s.position.lat - position.lat) < 0.4541958664785 && Math.abs(s.position.lng - position.lng) < 0.44915558749545)
          .sort((a, b) => Math.hypot(a.position.lat - position.lat, a.position.lng - position.lng) - Math.hypot(b.position.lat - position.lat, b.position.lng - position.lng));
        return true;
      }
    };
  }
  if (typeof module !== 'undefined' && module.exports) { module.exports = { createState, validPosition, sortStores, clearMap, pinOptions, sortByDistance, requestLocalPosition }; return; }
  let apiPromise;
  let apiAttempt = 0;
  function loadAPI(root) {
    if (window.google?.maps?.importLibrary) return Promise.resolve();
    if (apiPromise) return apiPromise;
    apiPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const callback = 'parkLocatorMapsReady' + (++apiAttempt);
      const url = new URL('https://maps.googleapis.com/maps/api/js');
      url.search = new URLSearchParams({ key: root.dataset.apiKey, v: root.dataset.version || 'weekly', language: root.dataset.language || 'da', region: root.dataset.region || 'DK', loading: 'async', callback });
      let timer;
      function fail() { clearTimeout(timer); script.remove(); window[callback] = () => {}; apiPromise = null; reject(new Error('Maps unavailable')); }
      window[callback] = () => { clearTimeout(timer); delete window[callback]; resolve(); };
      script.src = url.href; script.async = true; script.onerror = fail;
      script.nonce = document.querySelector('script[nonce]')?.nonce || '';
      timer = setTimeout(fail, 20000); document.head.append(script);
    });
    return apiPromise;
  }
  class ParkStoreLocator extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const on = (node, event, fn) => node?.addEventListener(event, fn, { signal: this.controller.signal });
      this.input = this.querySelector('[data-salon-name-search]');
      this.results = this.querySelector('[data-salon-name-results]');
      this.cards = this.querySelector('[data-salon-name-cards]');
      this.empty = this.querySelector('[data-salon-name-status]');
      this.error = this.querySelector('[data-salon-search-error]');
      this.back = this.querySelector('[data-salon-back]');
      this.mapCards = this.querySelector('.store-locator__map-stores');
      this.stores = Array.from(this.querySelectorAll('.store-locator-region-wrapper[data-regionname="all"] .store')).map((card, index) => ({
        id: String(index), card, order: card.dataset.order, region: card.dataset.locatorRegion || '',
        text: [card.textContent, card.dataset.address, card.dataset.name, card.dataset.region].join(' '),
        title: Array.from(card.querySelectorAll('h3,h4')).map(h => h.textContent.trim()).join(' '),
        position: { lat: Number.parseFloat(card.dataset.latitude), lng: Number.parseFloat(card.dataset.longitude) }
      }));
      this.stores = sortStores(this.stores);
      this.querySelectorAll('.store-list-wrapper').forEach(list => {
        sortStores(Array.from(list.querySelectorAll('.store')).map(card => ({ card, order: card.dataset.order }))).forEach(store => list.append(store.card));
      });
      this.state = createState(this.stores); this.markers = [];
      this.querySelectorAll('.store').forEach(card => card.classList.remove('!tw-hidden'));
      this.querySelector('.store-locator__loader')?.classList.add('!tw-hidden');
      on(this.input, 'input', () => this.filter());
      on(this.querySelector('[data-salon-search-form]'), 'submit', event => { event.preventDefault(); this.searchAddress(); });
      on(this.back, 'click', () => this.reset());
      this.querySelectorAll('.store-locator-region').forEach(button => on(button, 'click', event => { event.preventDefault(); this.reset(button.dataset.regionname); }));
      this.querySelectorAll('.store-locator-action-btn').forEach(button => on(button, 'click', event => {
        event.preventDefault();
        if (button.dataset.action === 'list') this.reset();
        else {
          const token = this.state.invalidate(); const lifetime = this.controller;
          delete this.dataset.searchMode; this.results.hidden = true; this.back.hidden = false; this.showView('map'); this.renderMapCards(this.state.visible);
          this.ensureMap().catch(() => { if (this.alive() && this.controller === lifetime && this.state.current(token)) this.showError(); });
        }
      }));
      this.reset();
      const lifetime = this.controller;
      requestLocalPosition(navigator.geolocation, this.state, () => this.alive() && this.controller === lifetime, position => {
        // Device coordinates only reorder the existing local cards. Never send them to Maps/Places.
        this.querySelectorAll('.store-list-wrapper').forEach(list => {
          sortByDistance(Array.from(list.querySelectorAll('.store')).map(card => ({ card, position: { lat: Number.parseFloat(card.dataset.latitude), lng: Number.parseFloat(card.dataset.longitude) } })), position).forEach(store => list.append(store.card));
        });
      });
    }
    disconnectedCallback() {
      this.state?.invalidate(); this.controller?.abort(); this.controller = null;
      clearMap(this); this.mapPromise = null;
    }
    alive() { return this.isConnected && this.controller && !this.controller.signal.aborted; }
    showError() { if (this.alive()) this.error.hidden = false; }
    clone(card) { const copy = card.cloneNode(true); copy.classList.remove('!tw-hidden'); copy.removeAttribute('style'); copy.removeAttribute('hidden'); copy.querySelectorAll('[id]').forEach(node => node.removeAttribute('id')); return copy; }
    showView(view) {
      this.state.view = view;
      this.querySelectorAll('.store-locator-content__type').forEach(node => node.setAttribute('aria-hidden', String(node.dataset.action !== view)));
      this.querySelectorAll('.store-locator-action-btn').forEach(node => node.setAttribute('aria-selected', String(node.dataset.action === view)));
    }
    renderMapCards(stores) {
      this.mapCards.replaceChildren(...stores.map(store => this.clone(store.card)));
      this.mapCards.setAttribute('aria-hidden', 'false');
      this.mapCards.scrollTop = 0;
      if (!stores.length) { const note = this.empty.cloneNode(true); note.hidden = false; note.removeAttribute('data-salon-name-status'); this.mapCards.append(note); }
    }
    reset(region = 'all') {
      this.state.reset(region); this.input.value = ''; delete this.dataset.searchMode;
      this.results.hidden = true; this.back.hidden = true; this.error.hidden = true; this.info?.close();
      this.querySelectorAll('.store-locator-region').forEach(node => node.setAttribute('aria-selected', String(node.dataset.regionname === region)));
      this.querySelectorAll('.store-locator-region-wrapper').forEach(node => node.setAttribute('aria-expanded', String(node.dataset.regionname === region)));
      this.querySelectorAll('.store').forEach(card => card.classList.remove('!tw-hidden'));
      this.showView('list'); this.renderMapCards(this.state.visible);
    }
    filter() {
      if (!this.input.value.trim()) { this.reset(); return this.stores; }
      this.error.hidden = true; this.info?.close(); this.dataset.searchMode = 'name';
      this.results.hidden = false; this.back.hidden = false;
      const found = this.state.search(this.input.value);
      this.cards.replaceChildren(...found.map(store => this.clone(store.card)));
      this.empty.hidden = found.length > 0; return found;
    }
    async searchAddress() {
      if (!this.input.value.trim() || this.filter().length) return;
      const token = this.state.invalidate(); const query = this.input.value.trim(); const lifetime = this.controller;
      try {
        await loadAPI(this);
        const places = await google.maps.importLibrary('places');
        if (!this.alive() || this.controller !== lifetime || !this.state.current(token)) return;
        const result = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({ input: query, includedRegionCodes: ['dk'], sessionToken: new places.AutocompleteSessionToken() });
        if (!this.alive() || this.controller !== lifetime || !this.state.current(token)) return;
        const place = result.suggestions[0]?.placePrediction?.toPlace();
        if (!place) throw new Error('No place');
        await place.fetchFields({ fields: ['location'] });
        if (!this.alive() || this.controller !== lifetime || !this.state.current(token)) return;
        const position = { lat: place.location?.lat(), lng: place.location?.lng() };
        if (!validPosition(position)) throw new Error('No location');
        if (!this.state.nearby(position, token)) return;
        this.dataset.searchMode = 'address'; this.results.hidden = true; this.showView('map'); this.renderMapCards(this.state.visible);
        await this.ensureMap();
        if (!this.alive() || this.controller !== lifetime || !this.state.current(token)) return;
        this.map.panTo(position); this.map.setZoom(10);
        if (matchMedia('(max-width: 767px)').matches) this.input.blur();
      } catch (_error) { if (this.alive() && this.controller === lifetime && this.state.current(token)) this.showError(); }
    }
    async ensureMap() {
      if (this.map) return;
      if (this.mapPromise) return this.mapPromise;
      const lifetime = this.controller;
      this.mapPromise = (async () => {
        await loadAPI(this);
        const [{ Map, InfoWindow }, { AdvancedMarkerElement, PinElement }] = await Promise.all([google.maps.importLibrary('maps'), google.maps.importLibrary('marker')]);
        if (!this.alive() || this.controller !== lifetime) return;
        this.map = new Map(this.querySelector('#map'), { center: { lat: 55.56682, lng: 9.75452 }, zoom: 8, mapId: this.dataset.mapId });
        this.info = new InfoWindow({ maxWidth: 300 });
        this.stores.filter(store => validPosition(store.position)).forEach(store => {
          const pin = new PinElement(pinOptions(this.dataset, location.href, matchMedia('(max-width: 767px)').matches));
          const marker = new AdvancedMarkerElement({ map: this.map, position: store.position, title: store.title, gmpClickable: true });
          this.markers.push(marker);
          marker.append(pin);
          marker.addEventListener('gmp-click', () => {
            this.state.select(store.id); delete this.dataset.searchMode; this.results.hidden = true; this.back.hidden = false;
            this.showView('map'); this.renderMapCards(this.state.visible);
            const content = document.createElement('div'); content.className = 'park-locator-popup';
            const heading = document.createElement('strong'); heading.className = 'park-locator-popup__heading'; heading.textContent = store.title; content.append(heading);
            store.card.querySelectorAll('.store-info > a:not(.park-salon-title-link),.store-info-btns a').forEach(link => {
              const url = new URL(link.href, location.href);
              if (!['https:', 'http:', 'tel:'].includes(url.protocol)) return;
              const row = document.createElement('div'); const copy = document.createElement('a'); copy.href = url.href; copy.textContent = link.textContent; copy.target = link.target; copy.rel = 'noopener'; row.append(copy); content.append(row);
            });
            this.info.close(); this.info.setContent(content); this.info.open({ map: this.map, anchor: marker });
          }, { signal: lifetime.signal });
        });
      })().catch(error => {
        if (this.controller === lifetime) { clearMap(this); this.mapPromise = null; this.querySelector('#map')?.replaceChildren(); }
        throw error;
      });
      return this.mapPromise;
    }
  }
  if (!customElements.get('park-store-locator')) customElements.define('park-store-locator', ParkStoreLocator);
}());
